import html2canvas from 'html2canvas';

export const VERSION = '0.1.0';

// ─── Size caps (dev-rules §8) ─────────────────────────────────────────────────
const CONSOLE_MAX_ENTRIES = 32;
const CONSOLE_MAX_ENTRY_BYTES = 4096;
const NETWORK_MAX_ENTRIES = 16;
const NETWORK_MAX_BODY_BYTES = 1024;
const SCREENSHOT_MAX_BYTES = 1_048_576;
const TRANSCRIPT_MSG_MAX_BYTES = 8192;

// ─── Types ────────────────────────────────────────────────────────────────────

type BugBuoyOptions = {
  apiKey?: string;
  apiUrl?: string;
};

type ConsoleLevel = 'error' | 'warn' | 'log';

interface ConsoleEntry {
  level: ConsoleLevel;
  message: string;
  ts: number;
}

interface NetworkEntry {
  method: string;
  url: string;
  status: number | null;
  body: string;
  ts: number;
}

interface Screenshot {
  dataUrl: string;
  takenAt: number;
}

interface MessageContext {
  console: ConsoleEntry[];
  network: NetworkEntry[];
  screenshot?: Screenshot;
}

// Classification envelope streamed by the server as the `classification` SSE event.
interface ClassificationEvent {
  classification?: string;
  duplicateOf?: string;
  confirmationRequired?: boolean;
}

// Report drafted from the last exchange, awaiting user confirmation.
interface PendingReport {
  text: string;
  context: MessageContext;
  classification: string | null;
  duplicateOf: string | null;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const STYLES = `
  :host {
    all: initial;
    font-family: system-ui, sans-serif;
  }
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  #buoy-btn {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #2563eb;
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2147483647;
    transition: background 0.15s;
  }
  #buoy-btn:hover {
    background: #1d4ed8;
  }
  #buoy-btn svg {
    width: 28px;
    height: 28px;
    fill: #fff;
  }
  #modal {
    position: fixed;
    bottom: 92px;
    right: 24px;
    width: 340px;
    max-height: 500px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.18);
    display: flex;
    flex-direction: column;
    z-index: 2147483646;
    overflow: hidden;
  }
  #modal.hidden {
    display: none;
  }
  #modal-header {
    background: #2563eb;
    color: #fff;
    padding: 12px 16px;
    font-size: 15px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  #close-btn {
    background: none;
    border: none;
    color: #fff;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    padding: 0 2px;
  }
  #transcript {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 200px;
  }
  .msg {
    max-width: 85%;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.4;
    word-break: break-word;
  }
  .msg.user {
    background: #e0e7ff;
    color: #1e1b4b;
    align-self: flex-end;
  }
  .msg.agent {
    background: #f1f5f9;
    color: #1e293b;
    align-self: flex-start;
  }
  #input-row {
    display: flex;
    border-top: 1px solid #e2e8f0;
    padding: 8px;
    gap: 8px;
  }
  #message-input {
    flex: 1;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 14px;
    outline: none;
    resize: none;
    font-family: inherit;
  }
  #message-input:focus {
    border-color: #2563eb;
  }
  #send-btn {
    background: #2563eb;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 8px 14px;
    font-size: 14px;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
  }
  #send-btn:hover {
    background: #1d4ed8;
  }
  #send-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
  #confirm-row {
    display: none;
    gap: 8px;
    padding: 8px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
    align-items: center;
  }
  #confirm-row.visible {
    display: flex;
  }
  #confirm-label {
    flex: 1;
    font-size: 12px;
    color: #475569;
  }
  #submit-report-btn {
    background: #16a34a;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 8px 14px;
    font-size: 14px;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
  }
  #submit-report-btn:hover {
    background: #15803d;
  }
  #submit-report-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
  @keyframes bb-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
    50%       { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
  }
  #mic-btn {
    background: #e2e8f0;
    border: none;
    border-radius: 6px;
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #475569;
    user-select: none;
  }
  #mic-btn svg {
    width: 18px;
    height: 18px;
    pointer-events: none;
  }
  #mic-btn:hover {
    background: #cbd5e1;
  }
  #mic-btn.recording {
    background: #dc2626;
    color: #fff;
    animation: bb-pulse 1.5s ease-in-out infinite;
  }
  .typing-indicator {
    display: flex;
    gap: 5px;
    padding: 10px 14px;
    align-self: flex-start;
    background: #f1f5f9;
    border-radius: 8px;
  }
  .typing-indicator span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #94a3b8;
    animation: bb-typing 1.2s ease-in-out infinite;
  }
  .typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
  .typing-indicator span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes bb-typing {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-4px); opacity: 1; }
  }
`;

// ─── SVG icon ─────────────────────────────────────────────────────────────────

const SVG_NS = 'http://www.w3.org/2000/svg';

function makeBuoyIcon(): SVGSVGElement {
  function svgEl<K extends keyof SVGElementTagNameMap>(
    tag: K,
    attrs: Record<string, string>,
  ): SVGElementTagNameMap[K] {
    const el = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    return el;
  }

  const svg = svgEl('svg', { viewBox: '0 0 24 24', xmlns: SVG_NS });
  svg.appendChild(
    svgEl('circle', {
      cx: '12',
      cy: '12',
      r: '9',
      fill: 'none',
      stroke: '#fff',
      'stroke-width': '2',
    }),
  );
  svg.appendChild(svgEl('circle', { cx: '12', cy: '12', r: '4', fill: '#fff' }));
  const lines: [string, string, string, string][] = [
    ['12', '3', '12', '7'],
    ['12', '17', '12', '21'],
    ['3', '12', '7', '12'],
    ['17', '12', '21', '12'],
  ];
  for (const [x1, y1, x2, y2] of lines) {
    svg.appendChild(
      svgEl('line', {
        x1,
        y1,
        x2,
        y2,
        stroke: '#fff',
        'stroke-width': '2',
        'stroke-linecap': 'round',
      }),
    );
  }
  return svg;
}

function makeMicIcon(): SVGSVGElement {
  function svgEl<K extends keyof SVGElementTagNameMap>(
    tag: K,
    attrs: Record<string, string>,
  ): SVGElementTagNameMap[K] {
    const el = document.createElementNS(SVG_NS, tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    return el;
  }
  const svg = svgEl('svg', { viewBox: '0 0 24 24', xmlns: SVG_NS });
  svg.appendChild(
    svgEl('rect', { x: '9', y: '2', width: '6', height: '12', rx: '3', fill: 'currentColor' }),
  );
  svg.appendChild(
    svgEl('path', {
      d: 'M5 11a7 7 0 0 0 14 0',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '2',
      'stroke-linecap': 'round',
    }),
  );
  svg.appendChild(
    svgEl('line', {
      x1: '12',
      y1: '18',
      x2: '12',
      y2: '21',
      stroke: 'currentColor',
      'stroke-width': '2',
      'stroke-linecap': 'round',
    }),
  );
  svg.appendChild(
    svgEl('line', {
      x1: '8',
      y1: '21',
      x2: '16',
      y2: '21',
      stroke: 'currentColor',
      'stroke-width': '2',
      'stroke-linecap': 'round',
    }),
  );
  return svg;
}

function makeTypingIndicator(): HTMLDivElement {
  const el = document.createElement('div');
  el.classList.add('typing-indicator');
  el.setAttribute('aria-label', 'Agent is typing');
  for (let i = 0; i < 3; i++) {
    el.appendChild(document.createElement('span'));
  }
  return el;
}

// ─── Speech recognition types (not yet in TS DOM lib) ────────────────────────

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
}

// ─── Widget ───────────────────────────────────────────────────────────────────

function createWidget(apiKey: string, apiUrl: string): { open: () => void; close: () => void } {
  // ── Capture buffers — closure-scoped per instance (dev-rules §3) ─────────
  const consoleBuffer: ConsoleEntry[] = [];
  const networkBuffer: NetworkEntry[] = [];
  let latestScreenshot: Screenshot | undefined;

  function truncate(s: string, maxBytes: number): string {
    const bytes = new TextEncoder().encode(s);
    if (bytes.length <= maxBytes) return s;
    return new TextDecoder().decode(bytes.slice(0, maxBytes)) + '[…truncated]';
  }

  function captureConsole(level: ConsoleLevel, args: unknown[]): void {
    const message = truncate(
      args.map((a) => (typeof a === 'string' ? a : String(a))).join(' '),
      CONSOLE_MAX_ENTRY_BYTES,
    );
    // Ring buffer: drop oldest when at cap
    if (consoleBuffer.length >= CONSOLE_MAX_ENTRIES) consoleBuffer.shift();
    consoleBuffer.push({ level, message, ts: Date.now() });
  }

  function captureNetwork(
    method: string,
    url: string,
    status: number | null,
    rawBody: string,
  ): void {
    if (networkBuffer.length >= NETWORK_MAX_ENTRIES) return;
    networkBuffer.push({
      method,
      url,
      status,
      body: truncate(rawBody, NETWORK_MAX_BODY_BYTES),
      ts: Date.now(),
    });
  }

  // ── Console interceptor: wrap, don't replace (dev-rules §3) ──────────────
  const origError = console.error;
  const origWarn = console.warn;
  const origLog = console.log;

  console.error = (...args: unknown[]) => {
    captureConsole('error', args);
    origError.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    captureConsole('warn', args);
    origWarn.apply(console, args);
  };
  console.log = (...args: unknown[]) => {
    captureConsole('log', args);
    origLog.apply(console, args);
  };

  // ── Fetch interceptor ─────────────────────────────────────────────────────
  let fetchWrapped = false;
  try {
    const origFetch = window.fetch.bind(window);
    window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
      let method = 'GET';
      let url = '';
      try {
        const input = args[0];
        if (typeof input === 'string') url = input;
        else if (input instanceof URL) url = input.toString();
        else if (input instanceof Request) {
          url = input.url;
          method = input.method.toUpperCase();
        }
        if (args[1]?.method) method = args[1].method.toUpperCase();
      } catch {
        /* silent — couldn't extract request metadata */
      }

      try {
        const response = await origFetch(...args);
        if (!response.ok && !url.startsWith(apiUrl)) {
          const bodyText = await response
            .clone()
            .text()
            .catch(() => '');
          captureNetwork(method, url, response.status, bodyText);
        }
        return response;
      } catch (err) {
        if (!url.startsWith(apiUrl)) captureNetwork(method, url, null, String(err));
        throw err;
      }
    };
    fetchWrapped = true;
  } catch {
    /* silent — fallback below */
  }

  if (!fetchWrapped) {
    // Passive fallback when fetch wrapping fails (dev-rules §3, silent degradation)
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      captureNetwork('UNKNOWN', '', null, String(e.reason));
    });
  }

  // ── XHR interceptor ───────────────────────────────────────────────────────
  try {
    const _OrigXHR = window.XMLHttpRequest;
    window.XMLHttpRequest = new Proxy(_OrigXHR, {
      construct(): object {
        const xhr = new _OrigXHR();
        let _method = 'GET';
        let _url = '';

        const origOpen = xhr.open.bind(xhr);
        // Patch open to capture method + URL before the request fires
        Object.defineProperty(xhr, 'open', {
          value(m: string, u: string, ...rest: unknown[]) {
            _method = m.toUpperCase();
            _url = u;
            return (origOpen as (...a: unknown[]) => void)(m, u, ...rest);
          },
          writable: true,
          configurable: true,
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 400 && !_url.startsWith(apiUrl)) {
            captureNetwork(
              _method,
              _url,
              xhr.status,
              truncate(String(xhr.responseText ?? ''), NETWORK_MAX_BODY_BYTES),
            );
          }
        });

        xhr.addEventListener('error', () => {
          if (!_url.startsWith(apiUrl)) captureNetwork(_method, _url, null, 'network error');
        });

        return xhr;
      },
    }) as unknown as typeof XMLHttpRequest;
  } catch {
    /* silent */
  }

  // ── Screenshot ────────────────────────────────────────────────────────────
  async function takeScreenshot(): Promise<Screenshot | undefined> {
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: false,
        allowTaint: false,
        scale: 0.5,
        ignoreElements: (el: Element) =>
          el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA',
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      if (dataUrl.length > SCREENSHOT_MAX_BYTES) return undefined;
      return { dataUrl, takenAt: Date.now() };
    } catch {
      return undefined; // silent degradation — CSP, tainted canvas, etc.
    }
  }

  async function collectContext(): Promise<MessageContext> {
    const screenshot = await takeScreenshot();
    if (screenshot) latestScreenshot = screenshot;
    const ctx: MessageContext = {
      console: [...consoleBuffer],
      network: [...networkBuffer],
    };
    if (latestScreenshot) ctx.screenshot = latestScreenshot;
    return ctx;
  }

  // ── Shadow DOM ────────────────────────────────────────────────────────────

  const host = document.createElement('div');
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'closed' });

  const style = document.createElement('style');
  style.textContent = STYLES;
  shadow.appendChild(style);

  const buoyBtn = document.createElement('button');
  buoyBtn.id = 'buoy-btn';
  buoyBtn.setAttribute('aria-label', 'Open Bug Buoy');
  buoyBtn.setAttribute('aria-expanded', 'false');
  buoyBtn.appendChild(makeBuoyIcon());
  shadow.appendChild(buoyBtn);

  const modal = document.createElement('div');
  modal.id = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-label', 'Bug Buoy chat');
  modal.classList.add('hidden');
  shadow.appendChild(modal);

  const header = document.createElement('div');
  header.id = 'modal-header';
  modal.appendChild(header);

  const titleEl = document.createElement('span');
  titleEl.textContent = 'Bug Buoy';
  header.appendChild(titleEl);

  const closeBtn = document.createElement('button');
  closeBtn.id = 'close-btn';
  closeBtn.setAttribute('aria-label', 'Close Bug Buoy');
  closeBtn.textContent = '×';
  header.appendChild(closeBtn);

  const transcript = document.createElement('div');
  transcript.id = 'transcript';
  transcript.setAttribute('aria-live', 'polite');
  modal.appendChild(transcript);

  const inputRow = document.createElement('div');
  inputRow.id = 'input-row';
  modal.appendChild(inputRow);

  const messageInput = document.createElement('textarea');
  messageInput.id = 'message-input';
  messageInput.setAttribute('placeholder', 'Describe your bug…');
  messageInput.setAttribute('rows', '2');
  messageInput.setAttribute('aria-label', 'Message');
  inputRow.appendChild(messageInput);

  const micBtn = document.createElement('button');
  micBtn.id = 'mic-btn';
  micBtn.setAttribute('aria-label', 'Hold to speak');
  micBtn.setAttribute('type', 'button');
  micBtn.appendChild(makeMicIcon());
  inputRow.appendChild(micBtn);

  const sendBtn = document.createElement('button');
  sendBtn.id = 'send-btn';
  sendBtn.textContent = 'Send';
  inputRow.appendChild(sendBtn);

  // Confirmation bar — hidden until the server's classification event arrives.
  // Submitting the report is gated on that event (confirmationRequired flow).
  const confirmRow = document.createElement('div');
  confirmRow.id = 'confirm-row';
  modal.appendChild(confirmRow);

  const confirmLabel = document.createElement('span');
  confirmLabel.id = 'confirm-label';
  confirmRow.appendChild(confirmLabel);

  const submitReportBtn = document.createElement('button');
  submitReportBtn.id = 'submit-report-btn';
  submitReportBtn.textContent = 'Submit report';
  confirmRow.appendChild(submitReportBtn);

  // ── Speech recognition ────────────────────────────────────────────────────

  const _w = window as Window & {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  };
  const SpeechRecognitionCtor = _w.SpeechRecognition ?? _w.webkitSpeechRecognition ?? null;

  if (!SpeechRecognitionCtor) {
    micBtn.style.display = 'none';
  }

  let activeRecognition: SpeechRecognition | null = null;
  let isRecording = false;

  function startRecording(): void {
    if (isRecording || !SpeechRecognitionCtor) return;
    const rec = new SpeechRecognitionCtor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || 'en-US';

    const priorText = messageInput.value;

    rec.onresult = (e: SpeechRecognitionEvent) => {
      let finalized = '';
      let interim = '';
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i];
        if (!r) continue;
        const alt = r[0];
        if (!alt) continue;
        if (r.isFinal) finalized += alt.transcript;
        else interim = alt.transcript;
      }
      messageInput.value = priorText + finalized + interim;
    };

    rec.onend = () => {
      isRecording = false;
      activeRecognition = null;
      micBtn.classList.remove('recording');
      micBtn.setAttribute('aria-label', 'Hold to speak');
    };

    rec.onerror = () => {
      isRecording = false;
      activeRecognition = null;
      micBtn.classList.remove('recording');
      micBtn.setAttribute('aria-label', 'Hold to speak');
    };

    try {
      rec.start();
      activeRecognition = rec;
      isRecording = true;
      micBtn.classList.add('recording');
      micBtn.setAttribute('aria-label', 'Release to stop');
    } catch {
      /* silent — permission denied or already active */
    }
  }

  function stopRecording(): void {
    if (!isRecording || !activeRecognition) return;
    activeRecognition.stop();
  }

  // ── Modal controls ────────────────────────────────────────────────────────

  function openModal(): void {
    modal.classList.remove('hidden');
    buoyBtn.setAttribute('aria-expanded', 'true');
    messageInput.focus();
  }

  function closeModal(): void {
    modal.classList.add('hidden');
    buoyBtn.setAttribute('aria-expanded', 'false');
  }

  function appendMessage(text: string, role: 'user' | 'agent'): void {
    const msg = document.createElement('div');
    msg.classList.add('msg', role);
    msg.textContent = text;
    transcript.appendChild(msg);
    transcript.scrollTop = transcript.scrollHeight;
  }

  // ── Session lifecycle ─────────────────────────────────────────────────────

  let sessionId: string | null = null;
  let sessionPromise: Promise<string | null> | null = null;
  let pendingReport: PendingReport | null = null;

  // Lazily create a server session on first send; concurrent callers share one
  // in-flight request. Fail-open: a failed start degrades to sessionId = null
  // (chat still works; report submission stays gated).
  function ensureSession(): Promise<string | null> {
    if (sessionId) return Promise.resolve(sessionId);
    if (sessionPromise) return sessionPromise;
    sessionPromise = (async () => {
      try {
        const resp = await fetch(`${apiUrl}/v1/session/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-BugBuoy-Key': apiKey,
          },
        });
        if (!resp.ok) return null;
        const data = (await resp.json()) as { sessionId?: string };
        sessionId = typeof data.sessionId === 'string' ? data.sessionId : null;
        return sessionId;
      } catch {
        return null;
      } finally {
        sessionPromise = null;
      }
    })();
    return sessionPromise;
  }

  function hideConfirmBar(): void {
    confirmRow.classList.remove('visible');
    pendingReport = null;
  }

  function showConfirmBar(report: PendingReport): void {
    pendingReport = report;
    confirmLabel.textContent = report.classification
      ? `Classified as “${report.classification}”. Submit this report?`
      : 'Submit this report?';
    submitReportBtn.disabled = false;
    confirmRow.classList.add('visible');
    transcript.scrollTop = transcript.scrollHeight;
  }

  // ── Submit: POST confirmed report (requires a live session) ───────────────

  async function submitReport(): Promise<void> {
    if (!pendingReport) return;
    submitReportBtn.disabled = true;

    const sid = await ensureSession();
    if (!sid) {
      appendMessage('Could not reach Bug Buoy server — report not submitted.', 'agent');
      submitReportBtn.disabled = false;
      return;
    }

    const { text, context, classification, duplicateOf } = pendingReport;
    let resp: Response;
    try {
      resp = await fetch(`${apiUrl}/v1/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-BugBuoy-Key': apiKey,
        },
        body: JSON.stringify({
          sessionId: sid,
          text,
          ...(classification !== null && { classification }),
          ...(duplicateOf !== null && { duplicateOf }),
          context,
        }),
      });
    } catch {
      appendMessage('Could not reach Bug Buoy server — report not submitted.', 'agent');
      submitReportBtn.disabled = false;
      return;
    }

    if (!resp.ok) {
      appendMessage('Server error submitting report — please try again.', 'agent');
      submitReportBtn.disabled = false;
      return;
    }

    let reportId = '';
    try {
      const data = (await resp.json()) as { reportId?: string };
      if (typeof data.reportId === 'string') reportId = data.reportId;
    } catch {
      /* body optional — treat 2xx as success */
    }
    hideConfirmBar();
    appendMessage(
      reportId
        ? `Report submitted — id ${reportId.slice(0, 8)}. Thank you!`
        : 'Report submitted. Thank you!',
      'agent',
    );
  }

  // ── Send: collect context + POST to API + stream SSE reply ────────────────

  async function handleSend(): Promise<void> {
    const raw = messageInput.value.trim();
    if (!raw) return;

    const text = truncate(raw, TRANSCRIPT_MSG_MAX_BYTES);
    sendBtn.disabled = true;
    hideConfirmBar(); // a new message invalidates the previous draft report
    appendMessage(text, 'user');
    messageInput.value = '';

    const context = await collectContext();
    const sid = await ensureSession();

    const typingEl = makeTypingIndicator();
    transcript.appendChild(typingEl);
    transcript.scrollTop = transcript.scrollHeight;

    let resp: Response;
    try {
      resp = await fetch(`${apiUrl}/v1/session/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-BugBuoy-Key': apiKey,
        },
        body: JSON.stringify({
          ...(sid !== null && { sessionId: sid }),
          message: text,
          context,
        } satisfies { sessionId?: string; message: string; context: MessageContext }),
      });
    } catch {
      typingEl.remove();
      appendMessage('Could not reach Bug Buoy server.', 'agent');
      sendBtn.disabled = false;
      messageInput.focus();
      return;
    }

    if (!resp.ok || !resp.body) {
      typingEl.remove();
      appendMessage('Server error — please try again.', 'agent');
      sendBtn.disabled = false;
      messageInput.focus();
      return;
    }

    // Stream SSE response into the agent message bubble
    typingEl.remove();
    const agentMsg = document.createElement('div');
    agentMsg.classList.add('msg', 'agent');
    transcript.appendChild(agentMsg);
    transcript.scrollTop = transcript.scrollHeight;

    try {
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let sseBuf = '';
      let agentText = '';
      let done = false;

      while (!done) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        sseBuf += decoder.decode(value, { stream: true });
        const events = sseBuf.split('\n\n');
        sseBuf = events.pop() ?? '';

        for (const raw of events) {
          if (!raw.trim()) continue;
          let eventType = 'message';
          let dataLine = '';
          for (const line of raw.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataLine = line.slice(6);
          }
          if (eventType === 'done') {
            done = true;
            break;
          }
          if (eventType === 'error') {
            if (!agentMsg.textContent) {
              agentMsg.textContent = 'Sorry, the assistant is unavailable right now.';
            }
            done = true;
            break;
          }
          if (eventType === 'classification' && dataLine) {
            // Server classified the report — unlock the confirm/submit step.
            try {
              const cls = JSON.parse(dataLine) as ClassificationEvent;
              showConfirmBar({
                text,
                context,
                classification: typeof cls.classification === 'string' ? cls.classification : null,
                duplicateOf: typeof cls.duplicateOf === 'string' ? cls.duplicateOf : null,
              });
            } catch {
              /* malformed classification — Submit stays gated */
            }
          }
          if (eventType === 'message' && dataLine) {
            try {
              const chunk = JSON.parse(dataLine) as { text?: string };
              if (typeof chunk.text === 'string') {
                agentText += chunk.text;
                agentMsg.textContent = agentText;
                transcript.scrollTop = transcript.scrollHeight;
              }
            } catch {
              /* malformed SSE data — skip chunk */
            }
          }
          if (eventType === 'tts_text' && dataLine) {
            let ttsText = dataLine;
            try {
              const parsed = JSON.parse(dataLine) as { text?: string };
              if (typeof parsed.text === 'string') ttsText = parsed.text;
            } catch {
              /* fallback to raw dataLine */
            }
            if (ttsText && window.speechSynthesis) {
              window.speechSynthesis.speak(new SpeechSynthesisUtterance(ttsText));
            }
          }
        }
      }
    } catch {
      if (!agentMsg.textContent) agentMsg.textContent = 'Error reading response.';
    }

    sendBtn.disabled = false;
    messageInput.focus();
  }

  // ── Event listeners ───────────────────────────────────────────────────────

  buoyBtn.addEventListener('click', openModal);
  closeBtn.addEventListener('click', closeModal);

  // Mic: mouse (desktop)
  micBtn.addEventListener('mousedown', (e: MouseEvent) => {
    e.preventDefault(); // keep focus in textarea
    startRecording();
  });
  micBtn.addEventListener('mouseup', stopRecording);
  micBtn.addEventListener('mouseleave', stopRecording);

  // Mic: touch (mobile)
  micBtn.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      e.preventDefault();
      startRecording();
    },
    { passive: false },
  );
  micBtn.addEventListener('touchend', stopRecording);
  micBtn.addEventListener('touchcancel', stopRecording);

  // Mic: keyboard (Space while focused)
  micBtn.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === ' ' && !isRecording) {
      e.preventDefault();
      startRecording();
    }
  });
  micBtn.addEventListener('keyup', (e: KeyboardEvent) => {
    if (e.key === ' ') stopRecording();
  });

  sendBtn.addEventListener('click', () => {
    void handleSend();
  });
  submitReportBtn.addEventListener('click', () => {
    void submitReport();
  });
  messageInput.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  });

  return { open: openModal, close: closeModal };
}

// ─── Public API ───────────────────────────────────────────────────────────────

const _scriptEl = document.currentScript as HTMLScriptElement | null;

let _open: (() => void) | null = null;
let _close: (() => void) | null = null;
let _initialized = false;

function init(opts?: BugBuoyOptions): void {
  if (_initialized) return;
  _initialized = true;

  const apiKey = opts?.apiKey ?? _scriptEl?.dataset['apiKey'] ?? '';
  // Defaut: l'origine d'ou le script a ete servi. Une bouee chargee depuis
  // https://exemple.com/widget.js parle a https://exemple.com — c'est le seul
  // defaut qui ne casse pas en silence. L'ancien defaut etait localhost:3000:
  // sur un vrai site, le widget s'affichait et n'envoyait rien, sans erreur
  // visible pour l'utilisateur. data-api-url reste prioritaire.
  // null et non '' : ?? ne retombe que sur null/undefined, une chaine vide
  // court-circuiterait le dernier defaut.
  const origineDuScript: string | null = (() => {
    try {
      return _scriptEl?.src ? new URL(_scriptEl.src).origin : null;
    } catch {
      return null;
    }
  })();
  const apiUrl =
    opts?.apiUrl ?? _scriptEl?.dataset['apiUrl'] ?? origineDuScript ?? 'http://localhost:3000';

  const widget = createWidget(apiKey, apiUrl);
  _open = widget.open;
  _close = widget.close;
}

function open(): void {
  if (_open) _open();
}

function close(): void {
  if (_close) _close();
}

function autoInit(): void {
  if (_scriptEl?.dataset['apiKey'] !== undefined) {
    init();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}

declare global {
  interface Window {
    BugBuoy: {
      init: (opts?: BugBuoyOptions) => void;
      open: () => void;
      close: () => void;
      version: string;
    };
  }
}

window.BugBuoy = { init, open, close, version: VERSION };
